import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/seo/SEOHead';
import {
  Droplets,
  Brain,
  Smartphone,
  BarChart3,
  Bell,
  Shield,
  Globe,
  ChevronRight,
  FileText,
  Check,
  Zap,
} from 'lucide-react';

export const LandingPage = () => {
  const { t } = useTranslation('landing');
  const latestFeatures = [
    {
      title: t('whatsNew.features.aiAssistant.title'),
      description: t('whatsNew.features.aiAssistant.description'),
      highlight: true,
      icon: '/ai-icon-small.png',
    },
    {
      title: t('whatsNew.features.snapshotSharing.title'),
      description: t('whatsNew.features.snapshotSharing.description'),
      highlight: true,
    },
    {
      title: t('whatsNew.features.security.title'),
      description: t('whatsNew.features.security.description'),
      highlight: true,
    },
    {
      title: t('whatsNew.features.languages.title'),
      description: t('whatsNew.features.languages.description'),
      highlight: true,
    },
  ];

  const features = [
    {
      icon: <Droplets className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.tracking.title'),
      description: t('features.list.tracking.description'),
    },
    {
      icon: <FileText className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.voidingDiary.title'),
      description: t('features.list.voidingDiary.description'),
    },
    {
      icon: <img src="/ai-icon-small.png" alt="AI" className="h-8 w-8" />,
      title: t('features.list.aiAssistant.title'),
      description: t('features.list.aiAssistant.description'),
    },
    {
      icon: <Brain className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.insights.title'),
      description: t('features.list.insights.description'),
    },
    {
      icon: <Bell className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.reminders.title'),
      description: t('features.list.reminders.description'),
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.analytics.title'),
      description: t('features.list.analytics.description'),
    },
    {
      icon: <Smartphone className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.pwa.title'),
      description: t('features.list.pwa.description'),
    },
    {
      icon: <Globe className="h-6 w-6 text-cyan-500" />,
      title: t('features.list.languages.title'),
      description: t('features.list.languages.description'),
    },
  ];

  const screenshots = [
    { src: '/screenshots/IMG_1355.PNG', alt: 'Home Dashboard', caption: t('screenshots.captions.daily') },
    { src: '/screenshots/IMG_1356.PNG', alt: 'Quick Entry', caption: t('screenshots.captions.entry') },
    { src: '/screenshots/IMG_1357.PNG', alt: 'History View', caption: t('screenshots.captions.history') },
    { src: '/screenshots/IMG_1358.PNG', alt: 'Analytics', caption: t('screenshots.captions.analytics') },
    { src: '/screenshots/IMG_1359.PNG', alt: 'Settings', caption: t('screenshots.captions.settings') },
    { src: '/screenshots/IMG_1360.PNG', alt: 'Multi-language', caption: t('screenshots.captions.multilang') },
  ];

  const pricingTiers = [
    {
      name: 'Free',
      price: '€0',
      duration: '36 hours',
      description: 'Get started tracking',
      features: [
        "Today's data access",
        '36-hour history',
        'Basic tracking',
      ],
      cta: 'Try Free',
      mostPopular: false,
      ctaLink: '/login',
    },
    {
      name: 'One-Time Pass',
      price: '€2.49',
      duration: '5 days access',
      description: 'Perfect for testing',
      features: [
        'Voice input',
        'Doctor sharing',
        'Full export',
        '5-day history',
      ],
      cta: 'Buy Now',
      mostPopular: false,
      ctaLink: '/pricing',
    },
    {
      name: 'Pro',
      price: '€3.99',
      duration: '/month',
      yearlyPrice: '€39.99/year',
      description: 'Full tracking power',
      features: [
        'Unlimited history',
        'Unlimited tasks',
        'Advanced reminders',
        'Priority support',
      ],
      cta: 'Subscribe',
      mostPopular: true,
      ctaLink: '/pricing',
    },
    {
      name: 'Premium',
      price: '€19.99',
      duration: '/month',
      yearlyPrice: '€149.99/year',
      description: 'AI-powered insights',
      features: [
        'Pro features + more',
        'AI analysis',
        'AI assistant',
        'Predictive insights',
      ],
      cta: 'Get Premium',
      mostPopular: false,
      ctaLink: '/pricing',
    },
  ];

  return (
    <>
      <SEOHead
        title="Track Your Urinary Wellness"
        description="AI-powered fluid intake and output tracking app. Get intelligent insights, reminders, and analytics to manage your urinary system wellness."
        keywords={[
          'fluid balance tracking',
          'fluid intake and output',
          'health monitoring app',
          'fluid balance monitor',
          'urinary wellness tracker',
          'health tracking',
          'urinary system wellness',
        ]}
        canonical="/"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-cyan-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {t('hero.title')}{' '}
              <span className="text-cyan-500">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app2.peetrack.live/login"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 px-10 py-5 text-xl font-bold text-white hover:bg-cyan-600 transition-all hover:scale-105 shadow-lg"
              >
                {t('hero.ctaPrimary')}
                <ChevronRight className="h-6 w-6" />
              </a>
              <a
                href="https://app2.peetrack.live/login"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-10 py-5 text-xl font-bold text-cyan-600 hover:bg-gray-50 transition-all hover:scale-105 shadow-lg border-2 border-cyan-200"
              >
                {t('hero.ctaDemo')}
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-sm">
              <span className="px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full font-semibold text-gray-700">
                🌐 {t('badges.languages')}
              </span>
              <span className="px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full font-semibold text-gray-700">
                📱 {t('badges.platforms')}
              </span>
              <span className="px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full font-semibold text-gray-700">
                🔒 {t('badges.hipaa')}
              </span>
              <span className="px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full font-semibold text-gray-700">
                🚀 {t('badges.pwa')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Features Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('whatsNew.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('whatsNew.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {latestFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="flex items-start gap-3">
                  {feature.icon ? (
                    <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center border-2 border-purple-200">
                      <img src={feature.icon} alt="" className="w-10 h-10" />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('screenshots.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('screenshots.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2"
              >
                <img
                  src={screenshot.src}
                  alt={screenshot.alt}
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-semibold text-center">{screenshot.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-cyan-50">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500 text-white text-2xl font-bold mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('howItWorks.steps.signup.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.steps.signup.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500 text-white text-2xl font-bold mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('howItWorks.steps.log.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.steps.log.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500 text-white text-2xl font-bold mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('howItWorks.steps.insights.title')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.steps.insights.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-lg text-gray-600">
              Flexible pricing options for everyone. Start free, upgrade when you're ready.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden transition-all ${
                  tier.mostPopular
                    ? 'ring-2 ring-cyan-500 shadow-2xl scale-105 md:scale-110'
                    : 'shadow-lg hover:shadow-xl hover:-translate-y-1'
                } ${tier.mostPopular ? 'bg-gradient-to-br from-cyan-50 to-blue-50' : 'bg-white'}`}
              >
                {/* Most Popular Badge */}
                {tier.mostPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 text-center font-bold text-sm flex items-center justify-center gap-2">
                    <Zap className="h-4 w-4" />
                    Most Popular
                  </div>
                )}

                <div className={`p-8 ${tier.mostPopular ? 'pt-16' : ''}`}>
                  {/* Tier Name */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-gray-600 text-sm mb-6">{tier.description}</p>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                      <span className="text-gray-600">{tier.duration}</span>
                    </div>
                    {tier.yearlyPrice && (
                      <p className="text-sm text-gray-500 mt-2">or {tier.yearlyPrice}</p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={tier.ctaLink}
                    className={`block w-full rounded-lg py-3 px-6 text-center font-semibold mb-8 transition-all hover:scale-105 ${
                      tier.mostPopular
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {tier.cta}
                  </Link>

                  {/* Features List */}
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-gray-700">Features included:</p>
                    <ul className="space-y-3">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            <Check className="h-5 w-5 text-cyan-500" />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing Note */}
          <div className="max-w-2xl mx-auto text-center mt-12">
            <p className="text-gray-600">
              All plans include a 36-hour free trial. No credit card required. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-cyan-500">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-lg text-cyan-50 mb-8">
              {t('cta.subtitle')}
            </p>
            <a
              href="https://app2.peetrack.live/login"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-4 text-lg font-semibold text-cyan-600 hover:bg-gray-50 transition-colors"
            >
              {t('cta.button')}
              <ChevronRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-white border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600 mb-4">
              <Shield className="h-5 w-5" />
              <span className="font-semibold">{t('trust.title')}</span>
            </div>
            <p className="text-sm text-gray-600">
              {t('trust.description')}{' '}
              <Link to="/privacy" className="text-cyan-600 hover:underline">
                {t('trust.privacyLink')}
              </Link>{' '}
              {t('trust.and')}{' '}
              <Link to="/terms" className="text-cyan-600 hover:underline">
                {t('trust.termsLink')}
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
};
